import React, { Suspense, lazy, useState, useEffect } from "react";
import { Link } from "react-scroll";
import Navbar from "./components/Navbar";
import Banner from "./components/Banner";
import SmoothScroll from "./components/SmoothScroll";
import ArrowUp from "./components/utils/logos/ArrowUp";
import { motion, useScroll } from "framer-motion";

// ── Above-fold: Navbar + Banner load immediately (synchronous) ────────────────
// ── Everything below fold: lazy-loaded separate JS chunks ────────────────────
// This eliminates ~2,900 KiB of unused JS from the initial bundle,
// directly cutting Total Blocking Time.

const About = lazy(() => import("./components/About"));
const Features = lazy(() => import("./components/Features"));
const Projects = lazy(() => import("./components/Projects"));
const EngineeringStories = lazy(() => import("./components/EngineeringStories"));
const SystemDesign = lazy(() => import("./components/SystemDesign"));
const OperationalExcellence = lazy(() => import("./components/OperationalExcellence"));
const Resume = lazy(() => import("./components/Resume"));
const HowIWork = lazy(() => import("./components/HowIWork"));
const AIEngineering = lazy(() => import("./components/AIEngineering"));
const ContinuousLearning = lazy(() => import("./components/ContinuousLearning"));
const BeyondCode = lazy(() => import("./components/BeyondCode"));
const Contacts = lazy(() => import("./components/Contacts"));
const LatestFooter = lazy(() => import("./components/LatestFooter"));
const FooterBottom = lazy(() => import("./components/FooterBottom"));

const SectionSkeleton = () => (
  <div className="w-full py-20 flex flex-col gap-10 xl:gap-16 border-b border-gray-800">
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="h-3 w-32 bg-gray-800/60 rounded-full animate-pulse" />
      <div className="h-10 w-64 md:w-96 bg-gray-800/80 rounded-lg animate-pulse" />
      <div className="h-3 w-48 md:w-72 bg-gray-800/40 rounded-full animate-pulse mt-2" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 xl:gap-10">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="w-full h-72 bg-gradient-to-br from-[#111827] to-[#0A0F1E] border border-gray-800/60 rounded-xl p-6 flex flex-col animate-pulse shadow-shadowOne"
        >
          <div className="w-full h-32 bg-gray-800/30 rounded-lg mb-4" />
          <div className="w-3/4 h-5 bg-gray-800/50 rounded mb-3" />
          <div className="w-full h-2.5 bg-gray-800/30 rounded mb-2" />
          <div className="w-5/6 h-2.5 bg-gray-800/30 rounded mb-2" />
          <div className="w-4/6 h-2.5 bg-gray-800/30 rounded mt-auto" />
        </div>
      ))}
    </div>
  </div>
);

const Prime = () => {
  const { scrollYProgress } = useScroll();
  const [showGoTop, setShowGoTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowGoTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div>
      <Navbar />
      <motion.div
        className="fixed top-20 left-0 right-0 h-[2px] bg-designColor origin-[0%] z-40"
        style={{ scaleX: scrollYProgress }}
        aria-hidden="true"
      />
      <SmoothScroll>
        <main className="max-w-screen-xl px-8 mx-auto overflow-x-hidden">
          <Banner />
          <Suspense fallback={<SectionSkeleton />}>
            <About />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <Features />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <Projects />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <EngineeringStories />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <SystemDesign />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <OperationalExcellence />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <Resume />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <HowIWork />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <AIEngineering />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <ContinuousLearning />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <BeyondCode />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <Contacts />
          </Suspense>
          <Suspense fallback={null}>
            <LatestFooter />
            <FooterBottom />
          </Suspense>
        </main>
      </SmoothScroll>

      {/* Go to top — lives OUTSIDE SmoothScroll so fixed positioning works correctly */}
      <Link
        className={`fixed bottom-5 right-5 z-50 flex items-center justify-center text-white rounded-full cursor-pointer w-11 h-11 bg-designColor hover:-translate-y-1 hover:shadow-lg hover:shadow-designColor/30 transition-all duration-300 ${showGoTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5 pointer-events-none'}`}
        title="Go to top"
        aria-label="Go to top"
        to="home"
        spy={true}
        smooth={true}
        offset={-70}
        duration={500}
      >
        <ArrowUp />
      </Link>
    </div>
  );
};

export default Prime;
